/**
 * A standard interface representing any paginated response from the backend.
 */
export interface PaginatedResponse {
  next?: string | null;
  previous?: string | null;
  count?: number;
}

const PAGE_REGEX = /[?&]page=(\d+)/;

/**
 * Parses the page number from a Django pagination URL.
 */
function parsePageParam(urlStr: string | null | undefined): number | undefined {
  if (!urlStr) {
    return undefined;
  }

  try {
    if (urlStr.startsWith("http://") || urlStr.startsWith("https://")) {
      const url = new URL(urlStr);
      const page = url.searchParams.get("page");
      return page ? Number.parseInt(page, 10) : undefined;
    }
  } catch {
    // Fallback if URL parsing fails
  }

  const match = PAGE_REGEX.exec(urlStr);
  return match ? Number.parseInt(match[1], 10) : undefined;
}

/**
 * A helper to extract the next page number from Django's `next` URL.
 * Designed to be passed directly as the `getNextPageParam` option to TanStack `useInfiniteQuery`.
 *
 * @example
 * const { data } = useCouponsListInfinite(params, {
 *   query: { getNextPageParam }
 * });
 *
 * @param lastPage The last fetched page of results.
 * @returns The next page number, or `undefined` if there are no more pages.
 */
export function getNextPageParam(lastPage: PaginatedResponse): number | undefined {
  return parsePageParam(lastPage.next);
}

/**
 * A helper to extract the previous page number from Django's `previous` URL.
 * Designed to be passed directly as the `getPreviousPageParam` option to TanStack `useInfiniteQuery`.
 *
 * @param firstPage The first page of results.
 * @returns The previous page number, or `undefined` if there is no previous page.
 */
export function getPreviousPageParam(firstPage: PaginatedResponse): number | undefined {
  return parsePageParam(firstPage.previous);
}
